import { Blend, CircleDollarSign, PlusCircle } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Image, Text, View, XStack, YStack } from "tamagui";
import { usePlanNavigation } from "../../../plans/usePlanNavigation";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Group } from "../../../ui/Group";
import { Label } from "../../../ui/Label";
import { Modal } from "../../../ui/Modal";
import { Subtext } from "../../../ui/Subtext";
import { TipsText } from "../../../ui/TipsText";
import { Title } from "../../../ui/Title";
import ExpenseItemModule from "../data/ExpenseItemModel";
import type ExpenseModuleModel from "../ExpenseModuleModel";
import { DebtsRow } from "./DebtsRow";
import { ExpenseHelper } from "./ExpenseHelper";
import { ExpenseRow } from "./ExpenseRow";
import { SettlementRow } from "./SettlementRow";

export const ExpenseModuleContent: React.FC<{ module: ExpenseModuleModel }> =
  observer(({ module }) => {
    const navigation = usePlanNavigation();

    const expensesAndSettlements = module.expensesAndSettlements;

    const [showDebtExplanaition, setShowDebtExplainationModal] =
      useState(false);

    const hasDebts = module.remainingDebts.length > 0;

    return (
      <>
        <YStack gap="$medium">
          <ExpenseHelper module={module} />

          <Button
            icon={PlusCircle}
            onPress={() =>
              navigation.push("Module", {
                planUid: module.plan.uid,
                moduleUid: module.uid,
                dataUid: "new",
              })
            }
          >
            Add expense
          </Button>

          {!hasDebts && module.expensesAndSettlements.length === 0 && (
            <Box>
              <YStack gap="$medium">
                <XStack gap="$small" alignItems="center">
                  <Blend />
                  <Title>Future debts will be seen here</Title>
                </XStack>
                <Body muted>
                  Once the first expense is added, you can settle up at any
                  time. We will ensure that transactions are kept to a minimum.
                </Body>
              </YStack>
            </Box>
          )}

          {!hasDebts && module.expensesAndSettlements.length > 0 && (
            <Box>
              <YStack gap="$medium">
                <XStack gap="$small" alignItems="center">
                  <Blend />
                  <Title>Debts</Title>
                </XStack>
                <Body muted>You are all settled at the moment. Great job</Body>
              </YStack>
            </Box>
          )}

          {hasDebts && (
            <Box>
              <YStack gap="$medium">
                <XStack gap="$small" alignItems="center">
                  <Blend />
                  <Title>Debts</Title>
                </XStack>
                {hasDebts && <Subtext>Tap on a row to settle debt</Subtext>}
              </YStack>

              <Group borderBottom>
                {module.remainingDebts.map((debt) => {
                  const creditorUser = debt.creditorUser;
                  const debtorUser = debt.debtorUser;

                  if (!debtorUser || !debtorUser) {
                    return null;
                  }

                  return (
                    <DebtsRow
                      key={(creditorUser?.uid ?? "") + (debtorUser?.uid ?? "")}
                      module={module}
                      debt={debt}
                    />
                  );
                })}
              </Group>
              <TipsText
                cursor="pointer"
                onPress={() => setShowDebtExplainationModal(true)}
                text="How does this work?"
              />
            </Box>
          )}

          <Box>
            <XStack gap="$small" alignItems="center">
              <CircleDollarSign />
              <Title>Transactions</Title>
            </XStack>
            {expensesAndSettlements.length ? (
              <Group>
                {expensesAndSettlements.map((expenseOrSettlement) =>
                  expenseOrSettlement instanceof ExpenseItemModule ? (
                    <ExpenseRow
                      module={module}
                      key={expenseOrSettlement.uid}
                      expense={expenseOrSettlement}
                    />
                  ) : (
                    <SettlementRow
                      module={module}
                      key={expenseOrSettlement.uid}
                      settlement={expenseOrSettlement}
                    />
                  ),
                )}
              </Group>
            ) : (
              <Body muted>
                No transactions recorded yet, add an expense to get started
              </Body>
            )}
          </Box>
        </YStack>
        <Modal
          modalId="expense_help"
          visible={showDebtExplanaition}
          onClose={() => setShowDebtExplainationModal(false)}
          header={<Title>How does this work?</Title>}
        >
          <YStack gap="$large">
            <Body>
              We use the 'greedy algorithm' to efficiently settle debts among
              users by minimizing the number of transactions required.
            </Body>
            <Body>Here's how it effectively manages debt repayments:</Body>
            <Text>
              <Label>1. Calculate Balances:</Label>{" "}
              <Body>
                The system first identifies how much each person has paid
                towards shared expenses and how much they owe, establishing a
                net balance for each individual. This determines who is a
                creditor (someone who should receive money) and who is a debtor
                (someone who owes money).
              </Body>
            </Text>
            <Text>
              <Label>2. Match Payments:</Label>{" "}
              <Body>
                It then matches debtors with creditors, starting from the
                largest debts and credits, settling these amounts fully or
                partially, depending on which is smaller. This process
                continues, systematically reducing the number of outstanding
                debts.
              </Body>
            </Text>
            <Text>
              <Label>3. Optimize Transactions:</Label>{" "}
              <Body>
                By settling the largest debts with the largest credits first,
                the algorithm reduces the overall number of transactions, making
                the settlement process simpler and more efficient for all
                parties involved.
              </Body>
            </Text>
            <Body>
              This approach ensures quick and straightforward debt resolution,
              focusing on effective and efficient settlements.
            </Body>
            <YStack gap="$small">
              <Label>Before</Label>
              <View
                padding="$large"
                borderWidth={1}
                borderColor={"$divider"}
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  aspectRatio={312 / 410}
                  objectFit="contain"
                  maxWidth={"100%"}
                  src={require("../../../../assets/expenses_before.png")}
                />
              </View>
              <Subtext>
                In this scenario without optimization, Viktor owes Johan $2000,
                and he also needs to pay Robin $4000. Meanwhile, Johan owes
                Robin $3000. This setup leads to multiple transactions: Viktor
                would pay $2000 to Johan and $4000 to Robin, while Johan would
                need to transfer $3000 to Robin. This process involves three
                separate payments and can be streamlined.
              </Subtext>
              <Label>After</Label>
              <View
                padding="$large"
                borderWidth={1}
                borderColor={"$divider"}
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  aspectRatio={312 / 410}
                  objectFit="contain"
                  maxWidth={"100%"}
                  src={require("../../../../assets/expenses_after.png")}
                />
              </View>

              <Subtext>
                By applying the greedy algorithm, we can reduce the number of
                transactions. Johan's $2000 debt from Viktor is used to offset
                part of Johan's $3000 debt to Robin, leaving Johan to pay Robin
                $1000. Viktor then pays Robin the remaining $6000 directly,
                which includes his own debt and the balance from Johan's debt.
                This optimized approach results in clearing all debts with just
                two transactions, simplifying the payment process significantly.
              </Subtext>
            </YStack>
          </YStack>
        </Modal>
      </>
    );
  });
